import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  RotateCcw,
  AlertTriangle,
  ShieldCheck,
  Tag,
  Layers,
  Crop,
  ExternalLink,
  Heart,
  Plus,
  Minus,
  Check,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { createEntry, listCollections } from "../lib/api";

const PLACEHOLDER =
  "https://images.unsplash.com/photo-1742743032379-8dbd2c2fe9df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxob2xvZ3JhcGhpYyUyMHRyYWRpbmclMjBjYXJkfGVufDB8fHx8MTc3NjQwNjExNnww&ixlib=rb-4.1.0&q=85";

const money = (n, currency = "USD") =>
  n === null || n === undefined
    ? "—"
    : new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 2 }).format(n);

export default function ResultsView({ result, onRescan, onRescanFooter, onCorrectAnchor }) {
  const { cards = [], total_value, message, mode } = result || {};
  return (
    <div className="min-h-full w-full px-5 sm:px-8 pt-28 pb-16 flex flex-col items-center">
      <div className="w-full max-w-5xl" data-testid="results-view">
        {/* Header bar */}
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="text-xs tracking-[0.22em] text-white/50 mb-2">
              {mode === "multi" ? `${cards.length} CARDS IDENTIFIED` : "SCAN RESULT"}
            </div>
            {mode === "multi" && total_value !== null && total_value !== undefined && (
              <div className="flex items-baseline gap-3">
                <h2 className="text-4xl sm:text-5xl font-light tracking-tighter text-white">
                  Total{" "}
                  <span className="gold font-normal" data-testid="total-value">
                    {money(total_value)}
                  </span>
                </h2>
              </div>
            )}
            {mode !== "multi" && cards[0] && (
              <h2 className="text-3xl sm:text-4xl font-light tracking-tighter text-white">
                {cards[0].name || "Unidentified card"}
              </h2>
            )}
          </div>
          <button
            type="button"
            onClick={onRescan}
            className="btn-primary flex items-center gap-2 text-sm"
            data-testid="rescan-btn"
          >
            <RotateCcw className="w-4 h-4" />
            Scan next card
          </button>
        </div>

        {message && (
          <div
            className="mb-6 flex items-start gap-3 px-4 py-3 rounded-2xl border border-amber-400/30 bg-amber-400/5"
            data-testid="low-confidence-banner"
          >
            <AlertTriangle className="w-4 h-4 gold mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-100/90">{message}</p>
          </div>
        )}

        {cards.length === 0 ? (
          <EmptyState onRescan={onRescan} />
        ) : (
          <div className="space-y-6">
            {cards.map((c, i) => (
              <CardResultCard
                key={c.id || i}
                card={c}
                onRescanFooter={onRescanFooter}
                onRescan={onRescan}
                onCorrectAnchor={onCorrectAnchor}
              />
            ))}
          </div>
        )}
      </div>

      {/* Sticky mobile CTA — always-visible "Scan next card" button on phones,
          because the page can get long with full result details. */}
      {cards.length > 0 && (
        <button
          type="button"
          onClick={onRescan}
          data-testid="sticky-scan-next-btn"
          className="sm:hidden fixed bottom-5 left-1/2 -translate-x-1/2 z-30 btn-primary text-sm flex items-center gap-2 shadow-[0_10px_30px_rgba(0,0,0,0.6)]"
        >
          <RotateCcw className="w-4 h-4" />
          Scan next card
        </button>
      )}
    </div>
  );
}

function EmptyState({ onRescan }) {
  return (
    <div className="glass rounded-3xl p-10 text-center">
      <p className="text-white/80">No Pokémon card detected in the image.</p>
      <p className="text-white/50 text-sm mt-2">
        Try a flat angle, better lighting, and keep all edges visible.
      </p>
      <button onClick={onRescan} className="btn-primary mt-6 text-sm">
        Retake photo
      </button>
    </div>
  );
}

function CardResultCard({ card, onRescanFooter, onRescan, onCorrectAnchor }) {
  const {
    status,
    name,
    name_native,
    set_name,
    set_code,
    card_number,
    rarity,
    language,
    variant,
    is_graded,
    grading_company,
    grade,
    cert_number,
    condition_estimate,
    confidence,
    needs_review,
    thumbnail_url,
    final_value,
    low_value,
    mid_value,
    high_value,
    sources = [],
    notes,
  } = card;

  // Uncertain / no-match state: show a clear "retake" call-to-action instead of a guessed card.
  if (status === "uncertain") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="glass rounded-3xl overflow-hidden"
        data-testid="card-uncertain"
      >
        <div className="p-8 sm:p-10 space-y-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 gold flex-shrink-0 mt-1" />
            <div>
              <div className="text-xs tracking-[0.22em] text-amber-200/70 mb-1">LOW CONFIDENCE</div>
              <h3 className="text-2xl sm:text-3xl font-medium tracking-tight text-white">
                Not confident enough to identify this card yet
              </h3>
            </div>
          </div>
          <p className="text-sm text-white/70 leading-relaxed">
            {notes || "Please retake the photo with better lighting, a flatter angle, and a closer crop — especially of the bottom edge of the card where the set code and card number are printed."}
          </p>
          {(language || set_code || card_number) && (
            <div className="pt-3 border-t border-white/10 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              {language && (
                <div><div className="text-white/40 tracking-wider mb-1">LANGUAGE</div><div className="text-white/85">{language}</div></div>
              )}
              {set_code && (
                <div><div className="text-white/40 tracking-wider mb-1">SET CODE</div><div className="text-white/85 font-mono">{set_code}</div></div>
              )}
              {card_number && (
                <div><div className="text-white/40 tracking-wider mb-1">NUMBER</div><div className="text-white/85 font-mono">{card_number}</div></div>
              )}
            </div>
          )}
          <div className="text-[11px] tracking-wider text-white/35">
            Confidence: {Math.round((confidence || 0) * 100)}%
          </div>

          <div className="pt-2 flex flex-col sm:flex-row gap-3">
            {onRescanFooter && (
              <button
                type="button"
                onClick={onRescanFooter}
                data-testid="rescan-footer-btn"
                className="btn-primary text-sm flex items-center justify-center gap-2"
              >
                <Crop className="w-4 h-4" />
                Rescan just the footer
              </button>
            )}
            {onRescan && (
              <button
                type="button"
                onClick={onRescan}
                data-testid="retake-full-btn"
                className="btn-ghost text-sm flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Retake full photo
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  const confPct = Math.round((confidence || 0) * 100);
  const thumb = thumbnail_url || PLACEHOLDER;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className="glass rounded-3xl overflow-hidden"
      data-testid="card-result"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
        {/* Thumbnail */}
        <div className="relative md:col-span-1 bg-black/40 p-6 flex items-center justify-center">
          <img
            src={thumb}
            alt={name || "Pokemon card"}
            className="w-full max-w-[260px] rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.55)]"
            data-testid="card-thumbnail"
          />
          {is_graded && (
            <div className="absolute top-4 left-4 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/70 border border-white/10 text-[11px] tracking-wider">
              <ShieldCheck className="w-3 h-3 gold" />
              {grading_company || "GRADED"} {grade ? `· ${grade}` : ""}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="md:col-span-2 p-6 sm:p-8 space-y-6">
          <div>
            {/* Anchor badge — shows the user what the app actually read from the footer.
                Click to correct a misread anchor and re-run the scan. */}
            {(set_code || card_number || language) && (
              <AnchorChip
                setCode={set_code}
                cardNumber={card_number}
                language={language}
                onCorrect={onCorrectAnchor}
              />
            )}
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h3 className="text-2xl sm:text-3xl font-medium tracking-tight text-white">
                  {name || "Unknown card"}
                </h3>
                <p className="text-sm text-white/50 mt-1">
                  {[set_name, card_number && `#${card_number}`, rarity].filter(Boolean).join(" · ") || "—"}
                </p>
              </div>
              <div className="text-right">
                <div className="text-[11px] tracking-[0.2em] text-white/50">FINAL VALUE</div>
                <div className="text-3xl font-normal gold tracking-tight" data-testid="final-value">
                  {money(final_value)}
                </div>
              </div>
            </div>
          </div>

          {/* Attributes grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <Attr label="Language" value={language} />
            <Attr label="Variant" value={variant} />
            <Attr label="Condition" value={condition_estimate} />
            {is_graded ? (
              <CertAttr company={grading_company} grade={grade} cert={cert_number} />
            ) : (
              <Attr label="Type" value="Raw" />
            )}
          </div>

          {/* Value range */}
          <div className="grid grid-cols-3 gap-3">
            <RangeTile label="Low" value={money(low_value)} />
            <RangeTile label="Mid" value={money(mid_value)} highlight />
            <RangeTile label="High" value={money(high_value)} />
          </div>

          {/* Confidence */}
          <div>
            <div className="flex items-center justify-between text-xs tracking-wider text-white/60 mb-2">
              <span>CONFIDENCE</span>
              <span className={needs_review ? "text-amber-300" : "text-emerald-300"} data-testid="confidence-value">
                {confPct}% {needs_review ? "· review" : ""}
              </span>
            </div>
            <div className="meter">
              <span style={{ width: `${Math.max(4, confPct)}%` }} />
            </div>
          </div>

          {/* Source breakdown */}
          {sources.length > 0 && (
            <div>
              <div className="text-xs tracking-[0.2em] text-white/50 mb-2 flex items-center gap-2">
                <Tag className="w-3.5 h-3.5" />
                SOURCE BREAKDOWN
              </div>
              <div className="divide-y divide-white/5 border border-white/5 rounded-2xl overflow-hidden">
                {sources.map((s, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <span className="text-white/80">
                      {s.url ? (
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline hover:text-white"
                        >
                          {s.source}
                        </a>
                      ) : (
                        s.source
                      )}
                    </span>
                    <span className="mono text-white/70 text-xs">
                      {s.low != null ? `L ${money(s.low)}` : ""}
                      {s.mid != null ? `  ·  M ${money(s.mid)}` : ""}
                      {s.high != null ? `  ·  H ${money(s.high)}` : ""}
                      {s.market != null && s.mid == null ? `M ${money(s.market)}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {notes && (
            <p className="text-xs text-white/45 italic">{notes}</p>
          )}

          {/* Post-scan action bar — lets the user log this card to a collection
              or wishlist in one tap, with variant/qty quick-picks. Hidden on
              "uncertain" results (handled above). */}
          <PostScanActions card={card} />
        </div>
      </div>
    </motion.div>
  );
}

function Attr({ label, value }) {
  return (
    <div className="px-3 py-2 rounded-xl bg-white/[0.03] border border-white/[0.06]">
      <div className="text-[10px] tracking-[0.18em] text-white/40">{label.toUpperCase()}</div>
      <div className="text-white/90 mt-0.5 text-sm truncate">{value || "—"}</div>
    </div>
  );
}

function CertAttr({ company, grade, cert }) {
  // Build the verification URL for the grading company, when one is known.
  const verifyUrl = (() => {
    if (!cert) return null;
    const c = String(cert).replace(/[^0-9A-Za-z]/g, "");
    const comp = (company || "").toUpperCase();
    if (comp === "PSA") return `https://www.psacard.com/cert/${c}/psacert`;
    if (comp === "BGS" || comp === "BECKETT")
      return `https://www.beckett.com/grading/card-lookup/?certid=${c}`;
    if (comp === "CGC") return `https://www.cgccards.com/certlookup/${c}/`;
    if (comp === "SGC") return `https://gosgc.com/cert/${c}`;
    return null;
  })();

  const display = [company, grade].filter(Boolean).join(" ") || "Graded";
  const sub = cert || "—";

  const inner = (
    <>
      <div className="text-[10px] tracking-[0.18em] text-amber-200/70">
        {display.toUpperCase()}
      </div>
      <div className="flex items-center gap-1.5 mt-0.5">
        <span className="text-amber-100 text-sm truncate font-mono" data-testid="cert-number-value">
          {sub}
        </span>
        {verifyUrl && <ExternalLink className="w-3 h-3 text-amber-300/80 flex-shrink-0" />}
      </div>
    </>
  );

  if (verifyUrl) {
    return (
      <a
        href={verifyUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="cert-verify-link"
        className="px-3 py-2 rounded-xl bg-amber-400/10 border border-amber-300/25 hover:bg-amber-400/15 hover:border-amber-300/45 transition-colors cursor-pointer"
        title={`Verify on ${company || "grading company"} website`}
      >
        {inner}
      </a>
    );
  }
  return (
    <div
      className="px-3 py-2 rounded-xl bg-amber-400/[0.05] border border-amber-300/15"
      data-testid="cert-attr-static"
    >
      {inner}
    </div>
  );
}


function RangeTile({ label, value, highlight }) {
  return (
    <div
      className={`px-4 py-3 rounded-2xl border ${
        highlight
          ? "bg-amber-400/10 border-amber-400/30"
          : "bg-white/[0.03] border-white/[0.06]"
      }`}
    >
      <div className="text-[10px] tracking-[0.2em] text-white/50">{label.toUpperCase()}</div>
      <div className={`mt-1 ${highlight ? "gold text-lg font-medium" : "text-white text-lg"}`}>
        {value}
      </div>
    </div>
  );
}


// ----------------------------------------------------------------------------
// AnchorChip — interactive amber chip that shows the footer anchors and
// expands into an inline correction form when the user taps "Tap to correct".
// Submitting the form re-runs the same scan with user-supplied overrides so
// misread OCR values can be fixed in 5 seconds without retaking the photo.
// ----------------------------------------------------------------------------
const LANGUAGES = [
  "English",
  "Japanese",
  "Korean",
  "Chinese (Traditional)",
  "Chinese (Simplified)",
  "German",
  "French",
  "Italian",
  "Spanish",
  "Portuguese",
];

function AnchorChip({ setCode, cardNumber, language, onCorrect }) {
  const [editing, setEditing] = React.useState(false);
  const [sc, setSc] = React.useState(setCode || "");
  const [cn, setCn] = React.useState(cardNumber || "");
  const [lg, setLg] = React.useState(language || "English");

  if (!editing) {
    return (
      <div className="mb-3 flex items-center gap-2 flex-wrap">
        <div
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-400/10 border border-amber-300/30 text-[10px] tracking-[0.14em] text-amber-100/90"
          data-testid="anchor-badge"
          title="These are the anchors the scanner read from the card's footer."
        >
          <span className="w-1.5 h-1.5 rounded-full bg-amber-300" />
          <span className="uppercase">Captured:</span>
          <span className="font-mono text-amber-100">
            {[setCode, cardNumber, language].filter(Boolean).join(" · ")}
          </span>
        </div>
        {onCorrect && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid="correct-anchor-btn"
            className="text-[10px] tracking-[0.14em] text-amber-300/70 hover:text-amber-200 underline underline-offset-4 decoration-amber-300/30 hover:decoration-amber-200/60 transition"
          >
            Looks wrong? Tap to correct.
          </button>
        )}
      </div>
    );
  }

  const submit = () => {
    onCorrect?.({
      set_code: sc.trim() || undefined,
      card_number: cn.trim() || undefined,
      language: lg || undefined,
    });
    setEditing(false);
  };

  return (
    <div
      data-testid="anchor-correct-form"
      className="mb-3 p-3 rounded-2xl bg-amber-400/[0.06] border border-amber-300/25 space-y-2"
    >
      <div className="text-[10px] tracking-[0.18em] text-amber-200/70 uppercase">
        Correct captured anchors
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <input
          type="text"
          value={sc}
          onChange={(e) => setSc(e.target.value)}
          placeholder="Set code (e.g. SV2a)"
          data-testid="correct-set-code"
          className="w-full px-2.5 py-1.5 rounded-lg bg-black/50 border border-amber-300/20 text-xs font-mono text-white placeholder:text-white/30 focus:outline-none focus:border-amber-300/60 transition"
        />
        <input
          type="text"
          value={cn}
          onChange={(e) => setCn(e.target.value)}
          placeholder="Number (e.g. 006/165)"
          data-testid="correct-card-number"
          className="w-full px-2.5 py-1.5 rounded-lg bg-black/50 border border-amber-300/20 text-xs font-mono text-white placeholder:text-white/30 focus:outline-none focus:border-amber-300/60 transition"
        />
        <select
          value={lg}
          onChange={(e) => setLg(e.target.value)}
          data-testid="correct-language"
          className="w-full px-2.5 py-1.5 rounded-lg bg-black/50 border border-amber-300/20 text-xs text-white focus:outline-none focus:border-amber-300/60 transition"
        >
          {LANGUAGES.map((l) => (
            <option key={l} value={l} className="bg-black">
              {l}
            </option>
          ))}
        </select>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={submit}
          data-testid="correct-submit-btn"
          className="btn-primary text-xs"
        >
          Rescan with fix
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          data-testid="correct-cancel-btn"
          className="btn-ghost text-xs"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// PostScanActions — variant & qty quick-picks + Add to Collection / Wishlist.
// On success, the button flips to a ✓ confirmation and stays that way so the
// user sees their scan was logged. Re-opening or scanning again resets it.
// The variant picker auto-hides when the card only has a single printing.
// ----------------------------------------------------------------------------
function PostScanActions({ card }) {
  // Auto-detect which variants are actually available for this card.
  // Backend populates `available_variants` from TCGplayer price keys; falls
  // back to ["Normal"] or ["Holo"] based on rarity when pricing is missing.
  const available = card?.available_variants?.length ? card.available_variants : [card?.variant || "Normal"];
  const initialVariant =
    card?.detected_variant && available.includes(card.detected_variant)
      ? card.detected_variant
      : available[0];

  const [variant, setVariant] = useState(initialVariant);
  const [qty, setQty] = useState(1);
  const [collections, setCollections] = useState([]);
  const [targetId, setTargetId] = useState(""); // "" = default (main / wishlist)
  const [savingKind, setSavingKind] = useState(null); // "collection" | "wishlist" | null
  const [addedKind, setAddedKind] = useState(null);
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    listCollections()
      .then((d) => setCollections(d.collections || []))
      .catch(() => setCollections([]));
  }, []);

  // Reset confirmation state if the card changes (user scans again).
  useEffect(() => {
    setAddedKind(null);
    setVariant(initialVariant);
    setQty(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id, card?.set_code, card?.card_number]);

  const buildPayload = (isWishlist) => ({
    collection_id: targetId || undefined,
    set_code: card.set_code || undefined,
    card_number: card.card_number || undefined,
    language: card.language || undefined,
    variant: variant || "Normal",
    name: card.name || undefined,
    name_native: card.name_native || undefined,
    thumbnail_url: card.thumbnail_url || undefined,
    rarity: card.rarity || undefined,
    quantity: Math.max(1, qty),
    condition: card.condition_estimate || "Near Mint",
    is_owned: !isWishlist,
    is_wishlist: isWishlist,
    price_mid: card.mid_value ?? card.final_value ?? null,
    price_low: card.low_value ?? null,
    price_high: card.high_value ?? null,
  });

  const save = async (isWishlist) => {
    const kind = isWishlist ? "wishlist" : "collection";
    setSavingKind(kind);
    try {
      await createEntry(buildPayload(isWishlist));
      setAddedKind(kind);
      toast.success(isWishlist ? "Added to wishlist." : "Added to collection.");
    } catch (e) {
      toast.error("Couldn't save. Try again?");
    } finally {
      setSavingKind(null);
    }
  };

  const eligibleCollections = collections.filter(
    (c) => c.type !== "wishlist"
  );
  const selectedName =
    collections.find((c) => c.id === targetId)?.name || "Main Collection";

  return (
    <div
      data-testid="post-scan-actions"
      className="mt-4 pt-5 border-t border-white/10 space-y-3"
    >
      <div className="text-[10px] tracking-[0.2em] text-amber-200/60 uppercase flex items-center gap-1.5">
        <Layers className="w-3 h-3" /> Log this card
      </div>

      {/* Variant chips — only render when more than one variant exists for
          this card. A single-variant card (e.g. most modern JP cards) silently
          locks to the detected variant. */}
      {available.length > 1 && (
        <div className="flex flex-wrap gap-1.5" data-testid="variant-chips">
          {available.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setVariant(v)}
              data-testid={`variant-${v.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-2.5 py-1 rounded-full text-[11px] tracking-wide transition ${
                variant === v
                  ? "bg-amber-400/15 text-amber-200 border border-amber-300/40"
                  : "bg-white/5 text-white/55 border border-transparent hover:bg-white/10"
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      )}
      {available.length <= 1 && (
        <div
          className="text-[11px] text-white/45"
          data-testid="variant-locked"
        >
          Variant: <span className="text-white/80">{variant}</span>
        </div>
      )}

      {/* Quantity + collection picker */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 bg-white/5 rounded-full px-1 py-0.5">
          <button
            type="button"
            onClick={() => setQty((q) => Math.max(1, q - 1))}
            data-testid="qty-dec"
            aria-label="decrease quantity"
            className="w-7 h-7 rounded-full hover:bg-white/10 text-white/70 flex items-center justify-center"
          >
            <Minus className="w-3 h-3" />
          </button>
          <span
            className="text-sm text-white w-6 text-center font-mono"
            data-testid="qty-value"
          >
            {qty}
          </span>
          <button
            type="button"
            onClick={() => setQty((q) => q + 1)}
            data-testid="qty-inc"
            aria-label="increase quantity"
            className="w-7 h-7 rounded-full hover:bg-white/10 text-white/70 flex items-center justify-center"
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>

        {/* Collection picker — lets power users route a scan to a specific
            non-wishlist collection (e.g. "Binder A"). Defaults to main. */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setPicking((p) => !p)}
            data-testid="pick-collection-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-white/5 hover:bg-white/10 text-white/80 border border-white/10"
          >
            <span className="truncate max-w-[140px]">{selectedName}</span>
            <ChevronDown className="w-3 h-3" />
          </button>
          {picking && (
            <div
              className="absolute left-0 mt-1 w-56 rounded-xl bg-black/95 border border-white/10 shadow-xl py-1 z-10"
              data-testid="collection-picker-menu"
            >
              {eligibleCollections.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    setTargetId(c.type === "main" ? "" : c.id);
                    setPicking(false);
                  }}
                  data-testid={`pick-collection-${c.id}`}
                  className="w-full text-left px-3 py-2 text-xs text-white/80 hover:bg-white/5"
                >
                  {c.name}{" "}
                  <span className="text-white/40">· {c.card_count || 0}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Primary action buttons */}
      <div className="flex flex-col sm:flex-row gap-2 pt-1">
        <button
          type="button"
          onClick={() => save(false)}
          disabled={savingKind !== null || addedKind === "collection"}
          data-testid="add-to-collection-btn"
          className="btn-primary text-sm flex-1 flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {addedKind === "collection" ? (
            <>
              <Check className="w-4 h-4" /> Added to {selectedName}
            </>
          ) : savingKind === "collection" ? (
            "Saving…"
          ) : (
            <>
              <Plus className="w-4 h-4" /> Add to Collection
            </>
          )}
        </button>
        <button
          type="button"
          onClick={() => save(true)}
          disabled={savingKind !== null || addedKind === "wishlist"}
          data-testid="add-to-wishlist-btn"
          className="btn-ghost text-sm flex-1 flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {addedKind === "wishlist" ? (
            <>
              <Check className="w-4 h-4" /> Added to Wishlist
            </>
          ) : savingKind === "wishlist" ? (
            "Saving…"
          ) : (
            <>
              <Heart className="w-4 h-4" /> Add to Wishlist
            </>
          )}
        </button>
      </div>
    </div>
  );
}

